import {
  countries as countriesList,
  getCountryData,
  TCountryCode,
} from 'countries-list';
import countriesI18N from 'i18n-iso-countries';
import de from 'i18n-iso-countries/langs/de.json';
import en from 'i18n-iso-countries/langs/en.json';
import fr from 'i18n-iso-countries/langs/fr.json';
import it from 'i18n-iso-countries/langs/it.json';
import { Languages } from '../types/lang.type';
import { languages } from './language.data';

countriesI18N.registerLocale(en);
countriesI18N.registerLocale(fr);
countriesI18N.registerLocale(de);
countriesI18N.registerLocale(it);

export type CountryEntry = {
  label: string;
  value: {
    ISO3: string;
    ISO2: string;
  };
};

const mapperLangCountry = {
  fr_FR: 'fr',
  en_EN: 'en',
  de_DE: 'de',
  it_IT: 'it',
} as const satisfies Record<Languages, string>;

const mapLangI18n = {
  fr_FR: 'fr',
  en_EN: 'en',
  de_DE: 'de',
  it_IT: 'it',
} as const satisfies Record<Languages, string>;

const countriesByLang = (lang: string): Array<CountryEntry> => {
  return Object.entries(countriesList)
    .map(([key, value]) => {
      const codeISO3 = getCountryData(key as TCountryCode)?.iso3;
      const codeISO2 = getCountryData(key as TCountryCode)?.iso2;

      return {
        label:
          countriesI18N.getName(
            codeISO3,
            mapperLangCountry[lang as keyof typeof mapperLangCountry] ||
              mapperLangCountry['fr_FR'],
          ) || value.name,
        value: {
          ISO3: codeISO3,
          ISO2: codeISO2,
        },
      };
    })
    .sort((a, b) => a.label.localeCompare(b.label));
};

export const countries: Record<string, Array<CountryEntry>> = {
  fr_FR: countriesByLang('fr_FR'),
  en_EN: countriesByLang('en_EN'),
  de_DE: countriesByLang('de_DE'),
  it_IT: countriesByLang('it_IT'),
};

export const getCountry = ({
  value,
  lang,
}: {
  value: string;
  lang: string;
}): { label: string | null; iso2: string | null; iso3: string | null } => {
  if (!(lang in mapLangI18n)) {
    throw new Error(`Lang ${lang} not supported`);
  }

  const type = value.length === 2 ? 'ISO2' : 'ISO3';

  switch (type) {
    case 'ISO2':
      return {
        label: countriesI18N.getName(value, mapLangI18n[lang]) || value,
        iso2: value,
        iso3: countriesI18N.alpha2ToAlpha3(value) || null,
      };
    case 'ISO3':
      return {
        label: countriesI18N.getName(value, mapLangI18n[lang]) || value,
        iso2: countriesI18N.alpha3ToAlpha2(value) || null,
        iso3: value,
      };
    default:
      throw new Error(`Type ${type} not supported`);
  }
};
